import { KPIS, METRIC_TYPES } from 'lumen/models/market-performance/data-item';
import {
  SORT_ON,
  type SortOn,
} from 'lumen/utils/constants/market-snapshots-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';

/**
 * Repoints an `orderBy` value (e.g. `sdly.revenue` or `-bi_forecast.adr`) from
 * one metric type to another, preserving the sub-metric and sort direction.
 *
 * Used when a swappable column (SDLY column, forecast) changes: a sort on the
 * old metric type would otherwise become orphaned (the API receives an
 * `order_by` for a metric no longer in `compare`). Returns the value unchanged
 * when it isn't sorted by `from` (including non-metric sorts like `name`).
 */
export function remapOrderByMetric(
  orderBy: string,
  from: string,
  to: string,
): string {
  const isDescending = orderBy.startsWith('-');
  const field = isDescending ? orderBy.slice(1) : orderBy;

  const separatorIndex = field.indexOf('.');
  if (separatorIndex === -1 || field.slice(0, separatorIndex) !== from) {
    return orderBy;
  }

  const remapped = `${to}${field.slice(separatorIndex)}`;
  return isDescending ? `-${remapped}` : remapped;
}

const ORDER_BY_PATTERN = new RegExp(
  `^(-?)(${METRIC_TYPES.join('|')})\\.totals\\.(${KPIS.join('|')})$`,
);

/** URL keeps `{metric}.totals.{kpi}`; returns `...variance.{kpi}.{percent|absolute}` when sorting on variance (not OTB), else unchanged. */
export function buildApiOrderBy(
  orderBy: string,
  sortOn: SortOn,
  varianceType: Variance,
): string {
  const match = ORDER_BY_PATTERN.exec(orderBy);
  if (!match || sortOn === SORT_ON.ACTUALS || match[2] === 'otb') {
    return orderBy;
  }

  const [, direction, metricType, kpi] = match;
  const suffix = varianceType === VARIANCES.ABSOLUTE ? 'absolute' : 'percent';

  return `${direction}${metricType}.totals.variance.${kpi}.${suffix}`;
}
